import React, { PropsWithChildren, useRef, useState } from "react";

import Drawer, {
  standardDrawer,
} from "@src/multiCustodian/components/Drawer/Drawer";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import DeleteModal from "@src/sharedComponents/Modal/DeleteModal/DeleteModal";
import ThreeDotsPopoverMenu from "@src/sharedComponents/Popover/ThreeDotsPopoverMenu/Menu";
import MenuItem from "@src/sharedComponents/Popover/ThreeDotsPopoverMenu/MenuItem";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./FormDrawer.module.css";

interface Props {
  title: string;
  subTitle?: string;
  drawerOpen: boolean;
  onClose: () => void;
  saveDisabled: boolean;
  onSave: () => void;
  removeDisabled: boolean;
  onRemove?: () => void;
  isLoading: boolean;
  hasError: boolean;
  deleteModalTitle: string;
  deleteModalSubTitle: string;
}

const FinancialGoalsFormDrawer = (props: PropsWithChildren<Props>) => {
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const onShowPopover = () => setShowPopover(true);
  const onHidePopover = () => setShowPopover(false);

  const handleOnDelete = () => {
    if (props.onRemove) {
      setShowDeleteModal(false);
      props.onRemove();
    }
  };

  const handleSetHideDeleteModal = () => setShowDeleteModal(false);

  const handleSetShowDeleteModal = () => {
    onHidePopover();
    setShowDeleteModal(true);
  };

  if (props.hasError) {
    return (
      <Drawer
        isDrawerOpen
        onClose={props.onClose}
        container_style={standardDrawer}
      >
        <div className={styles.error}>
          Error getting clients or checking permission
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer
      isDrawerOpen={props.drawerOpen}
      onClose={props.onClose}
      footer={
        <div className={styles.footer}>
          <Button
            text="Cancel"
            onClick={props.onClose}
            variant="outlined"
            buttonType="primary"
          />

          <Button
            text="Save"
            onClick={props.onSave}
            variant="contained"
            buttonType="primary"
            disabled={props.saveDisabled}
            sx={{ marginLeft: "var(--space-2)" }}
          />
        </div>
      }
      container_style={standardDrawer}
    >
      <div>
        <div className={styles.header}>
          <Text variant="title1" color="text">
            {props.title}
          </Text>

          {props.onRemove && props.removeDisabled !== true && (
            <ThreeDotsPopoverMenu
              className={styles.threeDotsPopoverMenu}
              anchorRef={anchorRef}
              isOpen={showPopover}
              onOpen={onShowPopover}
              onClose={onHidePopover}
            >
              <MenuItem
                className={styles.removeMenuItem}
                onClick={handleSetShowDeleteModal}
              >
                Remove Item
              </MenuItem>
            </ThreeDotsPopoverMenu>
          )}
        </div>

        {props.subTitle && (
          <Text variant="body" color="textSubtle">
            {props.subTitle}
          </Text>
        )}
      </div>

      {props.children}

      {props.isLoading && <LogoLoadingStill onTop={true} />}

      {showDeleteModal && (
        <DeleteModal
          title={props.deleteModalTitle}
          text={props.deleteModalSubTitle}
          deleteButtonText="Remove"
          onDelete={handleOnDelete}
          onClose={handleSetHideDeleteModal}
        />
      )}
    </Drawer>
  );
};

export default FinancialGoalsFormDrawer;
